/**
 * FrontendLogger - Client-side logging service for Grafana Loki integration.
 *
 * Captures frontend errors and important events, batches them, and sends
 * them to the backend relay endpoint (POST /api/v2/logs) for forwarding
 * to Grafana Loki.
 *
 * Usage as Vue plugin:
 *   import FrontendLogger from '@/modules/FrontendLogger'
 *   Vue.use(FrontendLogger, { enabled: true, minLevel: 'warn' })
 *
 * Access in components:
 *   this.$logger.info('Something happened', { extra: 'data' })
 */

const LOG_LEVELS = { debug: 0, info: 1, warn: 2, error: 3 };

const SENSITIVE_PATTERNS = [
  // Passwords
  /password[\s]*[:=][\s]*["']?[^\s"',}{]+/gi,
  /passwd[\s]*[:=][\s]*["']?[^\s"',}{]+/gi,
  /pwd[\s]*[:=][\s]*["']?[^\s"',}{]+/gi,
  // Tokens and secrets
  /token[\s]*[:=][\s]*["']?[^\s"',}{]+/gi,
  /api[_-]?key[\s]*[:=][\s]*["']?[^\s"',}{]+/gi,
  /secret[\s]*[:=][\s]*["']?[^\s"',}{]+/gi,
  /authorization[\s]*[:=][\s]*["']?[^\s"',}{]+/gi,
  // Credit card numbers (13-19 digits, optionally separated by spaces or dashes)
  /\b(?:\d[ -]*?){13,19}\b/g,
];

class FrontendLogger {
  constructor(config = {}) {
    this.enabled = config.enabled !== false;
    this.minLevel = config.minLevel || 'warn';
    this.batchSize = config.batchSize || 20;
    this.flushInterval = config.flushInterval || 10000;
    this.maxBufferSize = config.maxBufferSize || 100;
    this.endpoint = config.endpoint || '/api/v2/logs';
    this.isDevelopment = config.isDevelopment || false;
    this.buffer = [];
    this.timer = null;
    this._store = config.store || null;
    this._tenant = config.tenant || null;
    this._boundOnError = null;
    this._boundOnUnhandledRejection = null;
    this._boundOnBeforeUnload = null;
  }

  // --- Public API ---

  debug(message, meta = {}) {
    this._log('debug', message, meta);
  }

  info(message, meta = {}) {
    this._log('info', message, meta);
  }

  warn(message, meta = {}) {
    this._log('warn', message, meta);
  }

  error(message, meta = {}) {
    this._log('error', message, meta);
  }

  // --- Lifecycle ---

  /**
   * Vue plugin install method.
   * Called by Vue.use(FrontendLogger, options).
   */
  install(Vue) {
    const logger = this;

    // Make logger available as this.$logger in all components
    Vue.prototype.$logger = logger;

    // Register Vue error handler
    const originalErrorHandler = Vue.config.errorHandler;
    Vue.config.errorHandler = function (err, vm, info) {
      const componentName = vm && vm.$options && vm.$options.name;
      logger.error(`[Vue Error] ${err.message || err}`, {
        component: componentName || 'Unknown',
        info: info,
        stack: err.stack || null,
      });

      // Call original handler if it existed
      if (typeof originalErrorHandler === 'function') {
        originalErrorHandler.call(this, err, vm, info);
      }
    };
  }

  /**
   * Start the logger: begin flush interval and register global error handlers.
   */
  start() {
    if (!this.enabled) return;

    // Set up periodic flush
    this.timer = setInterval(() => this.flush(), this.flushInterval);

    // Register window.onerror
    this._boundOnError = (message, source, lineno, colno, error) => {
      this.error(`[window.onerror] ${message}`, {
        source,
        lineno,
        colno,
        stack: error && error.stack ? error.stack : null,
      });
    };
    window.addEventListener('error', this._boundOnError);

    // Register unhandledrejection
    this._boundOnUnhandledRejection = (event) => {
      const reason = event.reason;
      const message =
        reason instanceof Error ? reason.message : String(reason || 'Unknown rejection');
      this.error(`[Unhandled Rejection] ${message}`, {
        stack: reason instanceof Error ? reason.stack : null,
      });
    };
    window.addEventListener('unhandledrejection', this._boundOnUnhandledRejection);

    // Register beforeunload for final flush via sendBeacon
    this._boundOnBeforeUnload = () => {
      this._flushWithBeacon();
    };
    window.addEventListener('beforeunload', this._boundOnBeforeUnload);
  }

  /**
   * Stop the logger: flush remaining entries and clear timers/handlers.
   */
  stop() {
    if (this.timer) {
      clearInterval(this.timer);
      this.timer = null;
    }

    // Remove event listeners
    if (this._boundOnError) {
      window.removeEventListener('error', this._boundOnError);
      this._boundOnError = null;
    }
    if (this._boundOnUnhandledRejection) {
      window.removeEventListener('unhandledrejection', this._boundOnUnhandledRejection);
      this._boundOnUnhandledRejection = null;
    }
    if (this._boundOnBeforeUnload) {
      window.removeEventListener('beforeunload', this._boundOnBeforeUnload);
      this._boundOnBeforeUnload = null;
    }

    // Final flush
    this.flush();
  }

  /**
   * Immediately send all buffered entries to the relay endpoint.
   */
  flush() {
    if (this.buffer.length === 0) return;

    const entries = this.buffer.splice(0);
    this._send(entries);
  }

  // --- Private Methods ---

  _log(level, message, meta) {
    if (!this.enabled) return;
    if (!this._shouldLog(level)) return;

    // In development, also log to browser console
    if (this.isDevelopment && typeof console !== 'undefined') {
      const consoleFn = console[level] || console.log;
      consoleFn.call(console, `[FrontendLogger:${level}]`, message, meta);
    }

    this._enqueue(level, message, meta);
  }

  _shouldLog(level) {
    const levelValue = LOG_LEVELS[level];
    const minLevelValue = LOG_LEVELS[this.minLevel];
    if (levelValue === undefined || minLevelValue === undefined) return false;
    return levelValue >= minLevelValue;
  }

  _enqueue(level, message, meta) {
    const sanitizedMessage = this._sanitize(String(message || ''));
    const context = this._buildContext(meta);

    const entry = {
      level,
      message: sanitizedMessage,
      timestamp: new Date().toISOString(),
      context,
    };

    this.buffer.push(entry);

    // Enforce buffer cap — discard oldest entries
    while (this.buffer.length > this.maxBufferSize) {
      this.buffer.shift();
    }

    // Flush if batch size reached
    if (this.buffer.length >= this.batchSize) {
      this.flush();
    }
  }

  _buildContext(meta = {}) {
    const context = {
      url: typeof window !== 'undefined' ? window.location.href : '',
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      tenant: this._getTenant(),
      ...meta,
    };

    const userId = this._getUserId();
    if (userId) {
      context.userId = userId;
    }

    return context;
  }

  _getTenant() {
    if (this._tenant) return this._tenant;

    // Extract tenant from subdomain
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const parts = hostname.split('.');
      if (parts.length > 2) {
        return parts[0];
      }
    }
    return 'public';
  }

  _getUserId() {
    if (!this._store) return null;

    try {
      const currentUser = this._store.getters['Auth/currentUser'];
      return currentUser && currentUser.id ? currentUser.id : null;
    } catch (e) {
      return null;
    }
  }

  _sanitize(message) {
    let sanitized = message;
    for (const pattern of SENSITIVE_PATTERNS) {
      // Reset regex lastIndex for global patterns
      pattern.lastIndex = 0;
      sanitized = sanitized.replace(pattern, '[REDACTED]');
    }
    return sanitized;
  }

  _send(entries) {
    if (!entries || entries.length === 0) return;

    const payload = JSON.stringify({ entries });

    try {
      fetch(this.endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this._getCsrfToken(),
        },
        body: payload,
        keepalive: true,
      }).catch(() => {
        // On network failure, put entries back in buffer (up to max)
        this._requeue(entries);
      });
    } catch (e) {
      // fetch itself threw (e.g., in unsupported environments)
      this._requeue(entries);
    }
  }

  _flushWithBeacon() {
    if (this.buffer.length === 0) return;

    const entries = this.buffer.splice(0);
    const payload = JSON.stringify({ entries });

    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([payload], { type: 'application/json' });
      navigator.sendBeacon(this.endpoint, blob);
    } else {
      // Fallback to sync-ish fetch with keepalive
      this._send(entries);
    }
  }

  _requeue(entries) {
    // Put entries back at the front of the buffer, respecting max size
    this.buffer = entries.concat(this.buffer).slice(0, this.maxBufferSize);
  }

  _getCsrfToken() {
    if (typeof document === 'undefined') return '';
    const meta = document.querySelector("meta[name='csrf-token']");
    return meta ? meta.getAttribute('content') : '';
  }
}

export default FrontendLogger;
